import { Link, createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { Pencil, Plus, PowerOff, Power, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { ApiError } from '@/api/client';
import {
  useCreateMerchant,
  useDeleteMerchant,
  useMe,
  useMerchants,
  useUpdateMerchant,
} from '@/api/queries';
import type { Merchant } from '@/api/types';
import { Empty, ErrorBox } from '@/components/empty';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_authed/merchants/')({
  component: MerchantsPage,
});

const schema = z.object({
  code: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9_-]*$/, 'Lowercase letters, digits, _ and - only'),
  name: z.string().min(1).max(200),
});

type FormValues = z.infer<typeof schema>;

function MerchantsPage() {
  const { t } = useTranslation();
  const merchants = useMerchants();
  const me = useMe();
  const create = useCreateMerchant();
  const [createOpen, setCreateOpen] = useState(false);
  const [edit, setEdit] = useState<Merchant | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Merchant | null>(null);

  const limit = me.data?.entitlements?.limits.max_merchants;
  const tier = me.data?.entitlements?.tier;
  const atLimit =
    typeof limit === 'number' && (merchants.data?.length ?? 0) >= limit;

  const createForm = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { code: '', name: '' },
  });

  const onCreate = createForm.handleSubmit((values) => {
    create.mutate(values, {
      onSuccess: () => {
        toast.success(t('common.saved') as string);
        createForm.reset();
        setCreateOpen(false);
      },
      onError: (e) =>
        toast.error(e instanceof ApiError ? e.message : (t('common.failed') as string)),
    });
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('merchants.title')}</h1>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button
              disabled={atLimit}
              title={
                atLimit
                  ? (t('license.merchant_limit_reached', {
                      defaultValue:
                        'Tier {{tier}} allows {{limit}} merchant(s); contact your vendor to upgrade.',
                      tier,
                      limit,
                    }) as string)
                  : undefined
              }
            >
              <Plus className="h-4 w-4" /> {t('merchants.create')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={onCreate} className="space-y-4">
              <DialogHeader>
                <DialogTitle>{t('merchants.create')}</DialogTitle>
                <DialogDescription>
                  Code is the stable identifier in API logs and webhook URLs. Pick carefully —
                  it can be edited later but everything that referenced the old code stays as
                  a string in audit history.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5">
                <Label htmlFor="code">{t('merchants.code')}</Label>
                <Input id="code" autoFocus {...createForm.register('code')} />
                {createForm.formState.errors.code && (
                  <p className="text-xs text-destructive">
                    {createForm.formState.errors.code.message}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="name">{t('merchants.name')}</Label>
                <Input id="name" {...createForm.register('name')} />
                {createForm.formState.errors.name && (
                  <p className="text-xs text-destructive">
                    {createForm.formState.errors.name.message}
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={create.isPending}>
                  {t('common.create')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      {merchants.isError && <ErrorBox message={t('common.error')} />}

      <div className="rounded-xl border border-border bg-card shadow-soft overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('merchants.code')}</TableHead>
              <TableHead>{t('merchants.name')}</TableHead>
              <TableHead>{t('merchants.status')}</TableHead>
              <TableHead className="text-right">{t('merchants.payments_count')}</TableHead>
              <TableHead>{t('payments.cols.created')}</TableHead>
              <TableHead className="w-[1%] text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {merchants.data?.map((m) => (
              <MerchantRow
                key={m.id}
                m={m}
                onEdit={() => setEdit(m)}
                onDelete={() => setConfirmDelete(m)}
              />
            ))}
          </TableBody>
        </Table>
        {!merchants.isLoading && merchants.data?.length === 0 && (
          <Empty title={t('common.none')} className="border-0" />
        )}
      </div>

      <EditMerchantDialog merchant={edit} onClose={() => setEdit(null)} />
      <DeleteMerchantDialog merchant={confirmDelete} onClose={() => setConfirmDelete(null)} />
    </div>
  );
}

function MerchantRow({
  m,
  onEdit,
  onDelete,
}: {
  m: Merchant;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateMerchant(m.id);
  const toggling = update.isPending;
  const nextStatus = m.status === 'active' ? 'disabled' : 'active';

  const handleToggle = () => {
    update.mutate(
      { code: m.code, name: m.name, status: nextStatus },
      {
        onSuccess: () => toast.success(t('common.saved') as string),
        onError: (e) =>
          toast.error(e instanceof ApiError ? e.message : (t('common.failed') as string)),
      },
    );
  };

  const canDelete = m.payments_count === 0;

  return (
    <TableRow>
      <TableCell className="font-mono text-xs">
        <Link
          to="/merchants/$merchantId"
          params={{ merchantId: m.id }}
          className="hover:underline"
        >
          {m.code}
        </Link>
      </TableCell>
      <TableCell className="font-medium">{m.name}</TableCell>
      <TableCell>
        <Badge
          variant={
            m.status === 'active' ? 'success' : m.status === 'suspended' ? 'warning' : 'outline'
          }
        >
          {m.status === 'active'
            ? t('common.active')
            : m.status === 'suspended'
              ? t('common.suspended')
              : t('common.disabled')}
        </Badge>
      </TableCell>
      <TableCell className="text-end tabular-nums">{m.payments_count}</TableCell>
      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
        {dayjs(m.created_at).format('YYYY-MM-DD HH:mm')}
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-1">
          <ActionIconButton title={t('common.edit') as string} onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </ActionIconButton>
          <ActionIconButton
            title={(m.status === 'active' ? t('common.disable') : t('common.enable')) as string}
            onClick={handleToggle}
            disabled={toggling}
          >
            {m.status === 'active' ? (
              <PowerOff className="h-4 w-4" />
            ) : (
              <Power className="h-4 w-4 text-success" />
            )}
          </ActionIconButton>
          <ActionIconButton
            title={
              canDelete
                ? (t('common.delete') as string)
                : (t('merchants.delete_blocked_short', { count: m.payments_count }) as string)
            }
            onClick={onDelete}
            disabled={!canDelete}
            danger={canDelete}
          >
            <Trash2 className="h-4 w-4" />
          </ActionIconButton>
        </div>
      </TableCell>
    </TableRow>
  );
}

function ActionIconButton({
  title,
  onClick,
  disabled,
  danger,
  children,
}: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground',
        danger && 'hover:bg-destructive/10 hover:text-destructive',
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Edit dialog — reuses the create schema; status is editable here.
// ---------------------------------------------------------------------------

const editSchema = schema.extend({
  status: z.enum(['active', 'suspended', 'disabled']),
});
type EditValues = z.infer<typeof editSchema>;

function EditMerchantDialog({
  merchant,
  onClose,
}: {
  merchant: Merchant | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateMerchant(merchant?.id ?? '');
  const form = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    values: merchant
      ? { code: merchant.code, name: merchant.name, status: merchant.status }
      : { code: '', name: '', status: 'active' },
    resetOptions: { keepDirtyValues: false },
  });

  const onSubmit = form.handleSubmit((values) => {
    if (!merchant) return;
    update.mutate(values, {
      onSuccess: () => {
        toast.success(t('common.saved') as string);
        onClose();
      },
      onError: (e) =>
        toast.error(e instanceof ApiError ? e.message : (t('common.failed') as string)),
    });
  });

  return (
    <Dialog open={!!merchant} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t('merchants.edit')}</DialogTitle>
            <DialogDescription>{t('merchants.edit_description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="edit-code">{t('merchants.code')}</Label>
            <Input id="edit-code" {...form.register('code')} />
            {form.formState.errors.code && (
              <p className="text-xs text-destructive">{form.formState.errors.code.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-name">{t('merchants.name')}</Label>
            <Input id="edit-name" {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-status">{t('merchants.status')}</Label>
            <select
              id="edit-status"
              className="flex h-9 w-full rounded-md border border-input bg-card px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              {...form.register('status')}
            >
              <option value="active">{t('common.active')}</option>
              <option value="suspended">{t('common.suspended')}</option>
              <option value="disabled">{t('common.disabled')}</option>
            </select>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Delete confirmation. Hard delete — server cascades configuration tables.
// ---------------------------------------------------------------------------

function DeleteMerchantDialog({
  merchant,
  onClose,
}: {
  merchant: Merchant | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const remove = useDeleteMerchant();
  const [typedCode, setTypedCode] = useState('');
  const matches = merchant && typedCode === merchant.code;

  const onConfirm = () => {
    if (!merchant || !matches) return;
    remove.mutate(merchant.id, {
      onSuccess: () => {
        toast.success(t('common.deleted') as string);
        setTypedCode('');
        onClose();
      },
      onError: (e) =>
        toast.error(e instanceof ApiError ? e.message : (t('common.failed') as string)),
    });
  };

  return (
    <Dialog
      open={!!merchant}
      onOpenChange={(o) => {
        if (!o) {
          setTypedCode('');
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('merchants.delete_title')}</DialogTitle>
          <DialogDescription>{t('merchants.delete_confirm')}</DialogDescription>
        </DialogHeader>
        {merchant && (
          <div className="space-y-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <div>
              <span className="text-muted-foreground">{t('merchants.code')}: </span>
              <code className="font-mono text-foreground">{merchant.code}</code>
            </div>
            <div>
              <span className="text-muted-foreground">{t('merchants.name')}: </span>
              <span>{merchant.name}</span>
            </div>
            <div className="space-y-1.5 pt-2">
              <Label htmlFor="confirm-code">
                <code className="font-mono">{merchant.code}</code>
              </Label>
              <Input
                id="confirm-code"
                autoFocus
                value={typedCode}
                onChange={(e) => setTypedCode(e.target.value)}
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={!matches || remove.isPending}
          >
            {t('merchants.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
